import { ReactNode, useLayoutEffect, useMemo, useState } from "react";
import { NavLink } from "@/components/NavLink";
import { useLocation, Navigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { teamService } from "@/services/teamService";
import sophieAiLogo from "@/assets/sophie-ai-logo.png";
import "@/sophie.css";
import {
  LayoutDashboard,
  Bot,
  Building2,
  BookOpen,
  MessageSquare,
  CreditCard,
  Users,
  HelpCircle,
  Menu,
  X,
  Plug,
  MessageCircle,
  Settings as SettingsIcon,
  Mic,
  ShieldCheck,
  PhoneCall,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import UserMenu from "@/components/auth/UserMenu";
import { useAuth } from "@/contexts/AuthContext";
import { ALL_LOCATIONS_ID, useProjectSelection } from "@/contexts/ProjectSelectionContext";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

interface AppLayoutProps {
  children: ReactNode;
}

export function AppLayout({ children }: AppLayoutProps) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const { user } = useAuth();
  const isSuperAdmin = user?.teamRole === "super_admin";
  const isAdminLike = user?.teamRole === "admin" || isSuperAdmin;
  const { projects, selectedProjectId, setSelectedProjectId } = useProjectSelection();
  const sortedProjects = useMemo(
    () => [...projects].sort((a, b) => a.name.localeCompare(b.name)),
    [projects]
  );
  const shouldShowLocationSelect = sortedProjects.length >= 1;

  const { data: team, isLoading: isTeamLoading } = useQuery({
    queryKey: ['team'],
    queryFn: () => teamService.getTeam(),
    staleTime: 60000,
  });

  useLayoutEffect(() => {
    const root = document.documentElement;
    root.classList.add("sophie-theme");

    return () => {
      root.classList.remove("sophie-theme");
    };
  }, []);

  if (isTeamLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (team?.subscription === 1 && location.pathname !== '/billing' && location.pathname !== '/settings') {
    return <Navigate to="/billing" replace />;
  }

  const navItems = [
    { path: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
    ...(isAdminLike
      ? [
          { path: "/business", label: "Your Business", icon: Building2 },
          { path: "/voice-settings", label: "Voice Settings", icon: Mic },
          // { path: "/knowledge", label: "Knowledge Docs", icon: BookOpen },
        ]
      : []),
    { path: "/chat-history", label: "Call History", icon: PhoneCall },
    ...(isAdminLike
      ? [
          { path: "/integrations", label: "Integrations", icon: Plug },
        ]
      : []),
    { path: "/notifications", label: "Notifications", icon: MessageCircle },
    ...(isAdminLike
      ? [
          { path: "/billing", label: "Billing", icon: CreditCard },
          { path: "/team", label: "Team", icon: Users },
        ]
      : []),
    ...(isSuperAdmin
      ? [
          { path: "/demo", label: "Demo", icon: Bot },
          { path: "/superadmin", label: "Super Admin", icon: ShieldCheck },
        ]
      : []),
    { path: "/settings", label: "Settings", icon: SettingsIcon },
    { path: "/support", label: "Contact Support", icon: HelpCircle },
  ];

  return (
    <div className="min-h-screen bg-background w-full">
      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 h-16 bg-card border-b border-border z-50 flex items-center justify-between px-4">
        <div className="flex items-center">
          <img src={sophieAiLogo} alt="Sophie AI" className="h-10" />
        </div>
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
        >
          {mobileMenuOpen ? <X /> : <Menu />}
        </Button>
      </header>

      {/* Desktop Sidebar */}
      <aside className="hidden lg:block fixed left-0 top-0 bottom-0 w-64 bg-card border-r border-border">
        <div className="flex h-full flex-col p-6">
          <div className="flex items-center mb-8">
            <img src={sophieAiLogo} alt="Sophie AI" className="h-10" />
          </div>

          {shouldShowLocationSelect && (
            <div className="mb-6">
              <p className="text-xs uppercase tracking-wide text-muted-foreground mb-2">
                Location
              </p>
              <Select value={selectedProjectId} onValueChange={setSelectedProjectId}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="All Locations" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL_LOCATIONS_ID}>All Locations</SelectItem>
                  {sortedProjects.map((project) => (
                    <SelectItem key={project.id} value={project.id}>
                      {project.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <nav className="flex-1 space-y-2 overflow-y-auto pr-2">
            {navItems.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                end={item.path === "/"}
                className="flex items-center gap-3 px-4 py-3 rounded-xl text-muted-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground transition-all"
                activeClassName="bg-sidebar-accent text-sidebar-accent-foreground font-medium"
              >
                <item.icon className="w-5 h-5" />
                <span>{item.label}</span>
              </NavLink>
            ))}
          </nav>
        </div>
      </aside>

      {/* Mobile Navigation Overlay */}
      {mobileMenuOpen && (
        <div className="lg:hidden fixed inset-0 bg-background z-40 pt-16 overflow-y-auto">
          <nav className="p-4 space-y-2 pb-24">
            {shouldShowLocationSelect && (
              <div className="mb-4">
                <p className="text-xs uppercase tracking-wide text-muted-foreground mb-2">
                  Location
                </p>
                <Select value={selectedProjectId} onValueChange={setSelectedProjectId}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="All Locations" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={ALL_LOCATIONS_ID}>All Locations</SelectItem>
                    {sortedProjects.map((project) => (
                      <SelectItem key={project.id} value={project.id}>
                        {project.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            {navItems.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                end={item.path === "/"}
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center gap-3 px-4 py-3 rounded-xl text-muted-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground transition-all"
                activeClassName="bg-sidebar-accent text-sidebar-accent-foreground font-medium"
              >
                <item.icon className="w-5 h-5" />
                <span>{item.label}</span>
              </NavLink>
            ))}
          </nav>
        </div>
      )}

      {/* Main Content */}
      <main className="lg:ml-64 min-h-screen pt-16 lg:pt-0">
        {/* Top Bar */}
        <div className="sticky top-0 lg:top-0 z-30 bg-background border-b border-border">
          <div className="flex items-center justify-between h-16 px-6">
            <h1 className="text-2xl font-semibold hidden lg:block">
              {navItems.find((item) => item.path === location.pathname)?.label || "Dashboard"}
            </h1>
            <div className="flex items-center gap-4 ml-auto">
              <UserMenu />
            </div>
          </div>
        </div>

        {/* Page Content */}
        <div className="p-6">{children}</div>
      </main>

      {/* Mobile Bottom Navigation */}
      <nav className="lg:hidden fixed bottom-0 left-0 right-0 bg-card border-t border-border z-50">
        <div className="flex items-center justify-around h-16 px-2">
          {navItems.slice(0, 5).map((item) => {
            const isActive = location.pathname === item.path || 
              (item.path === "/" && location.pathname === "/");
            return (
              <NavLink
                key={item.path}
                to={item.path}
                end={item.path === "/"}
                className="flex flex-col items-center gap-1 px-3 py-2 rounded-lg text-muted-foreground hover:text-foreground transition-colors"
                activeClassName="text-primary"
              >
                <item.icon className="w-5 h-5" />
                <span className="text-xs">{item.label}</span>
              </NavLink>
            );
          })}
        </div>
      </nav>
    </div>
  );
}
